import React, { useEffect, useState, FC } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client'
import Toolbar from '@mui/material/Toolbar';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import MDButtonRoot from "./MDButton/MDButtonRoot";
import setThemeObj from './../hooks/useThemeMUI';
import MDBoxRoot from './MDBox/MDBoxRoot';
import StyledBox from "./StyleBox";

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import MDTypographyRoot from './MDTypography/MDTypographyRoot';

import EditClassDialog from './EditClassDialog';

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { DataGrid, GridColDef, GridToolbarContainer, GridActionsCellItem, GridRowId } from '@mui/x-data-grid';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import * as types from "../gql/graphql";

import supabase, { supabaseKey, supabaseUrl } from '../supabase';
import { graphql } from '../gql/gql';
import { useQueryAll } from '../apolloClient';

// useAllItems - TODO paginate on client?
const getClassesQuery = gql(`
  query GetClasses($after: Cursor) {
    fs_classesCollection(after: $after) {
        pageInfo { endCursor startCursor hasNextPage }    
        edges {
          node {
            nodeId
            id
            name
            duration
            code
            category
            fs_prodcategory { id catname }
            eventtype
            fs_eventtype { id typename }
            active 
            cancelcutoff 
            description 
            followupmsg
            listorder 
            notificationmsg 
            signcutoff 
            signupmsg 
            wlcutoff
            multi
          }
        }
      }
  }
`);
const deleteClassQuery = gql(`
    mutation deleteClassesQuery($filter:fs_classesFilter) {
        deleteFromfs_classesCollection(filter: $filter) {
            affectedCount
        }
    }
`);
const getEventsForClassQuery = gql(`
    query GetEventCountForClass($id: BigInt!) {
        get_event_count_for_class(p_id:$id)
  }
`)


interface EditToolbarProps {
    openModal: () => void;

}

function formatClasses(test1: types.Fs_ClassesEdge[]): any[] {
    return test1.map(
        (item: types.Fs_ClassesEdge) => {
            const subsetItem = {
                nodeId: item.node.nodeId,
                id: item.node.id,
                name: item.node.name,
                duration: item.node.duration,
                code: item.node.code,
                eventtype: item.node.eventtype,
                eventTypeName: item.node.fs_eventtype.typename,
                category: item.node.category,
                productCategory: item.node.fs_prodcategory.catname,
                active: item.node.active,
                cancelcutoff: item.node.cancelcutoff,
                description: item.node.description,
                followupmsg: item.node.followupmsg,
                listorder: item.node.listorder,
                notificationmsg: item.node.notificationmsg,
                signcutoff: item.node.signcutoff,
                signupmsg: item.node.signupmsg,
                wlcutoff: item.node.wlcutoff,
                multi: item.node.multi
            };
            return subsetItem;
        }
    ).sort((a, b) => {
        // First, compare by category in ascending order
        if (a.category < b.category) return -1;
        if (a.category > b.category) return 1;

        // If categories are the same, then compare by listorder in ascending order
        return a.listorder - b.listorder;
    });
}


function ClassesList() {

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'name', headerName: 'Class Name', width: 320 },
        { field: 'duration', headerName: 'Duration', type: 'number', editable: true, width: 140 },
        { field: 'code', headerName: 'Code', width: 110 },
        { field: 'eventTypeName', headerName: 'Event Type', width: 120 },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: (params) => {

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary icons-style"
                        onClick={handleEditClick(params.row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        className="textPrimary icons-style error"
                        onClick={handleDeleteClick(params.row)}
                        color="inherit"
                    />,
                ];
            }
        },
    ];

    const [editedRow, setEditedRow] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [classes, setClasses] = useState([]);
    //const { allItems: data, loading, error } = useAllItems<types.Fs_ClassesEdge>(getClassesQuery, { fetchPolicy: 'no-cache' });

    const { data, loading, error } = useQueryAll<types.Fs_ClassesEdge>({query: getClassesQuery});
    

    useEffect(() => {
        if (!loading && data) {
            const formatted = formatClasses(data);
            setClasses(formatted);
        }
    }, [data]);

    const handleAddClick = () => {
        setEditedRow(null); setIsModalOpen(true);
    };

    const handleEditClick = (row: any) => () => {
        setEditedRow(row);
        setIsModalOpen(true);
    };
    const handleSave = () => {
        // Handle saving the edited row data
        setIsModalOpen(false);
    };

    if (loading) return (<div>Loading</div>)
    if (error) return (<div>Error</div>)

    const handleDeleteClick = (row: any) => async () => {
        setEditedRow(row);
        setIsDeleteDialogOpen(true);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return; // was not completed 

        setClasses(prevClasses => {
            const reorderedClasses = Array.from(prevClasses);
            const [reorderedItem] = reorderedClasses.splice(result.source.index, 1);
            reorderedClasses.splice(result.destination.index, 0, reorderedItem);
            return reorderedClasses;
        });
    };
    return (
        <Grid container spacing={2}>
            <Grid item xs={11}>
                <Card>
                    <MDBoxRoot
                        component="main" px={4} py={6}
                        sx={{ flexGrow: 1, width: '100%' }}
                    >
                        <Toolbar />
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="data-grid" >
                                {(provided) => (
                                    <MDBoxRoot  ref={provided.innerRef} {...provided.droppableProps}>
                                        <StyledBox theme={setThemeObj} sx={{marginTop:"10px"}}>
                                            <MDButtonRoot
                                                theme={setThemeObj}
                                                ownerState={{
                                                    size: "medium",
                                                    variant: "text",
                                                    color: "info",
                                                    textTransform: "uppercase",
                                                }}
                                                sx={{marginBottom:'8px'}}
                                                onClick={handleAddClick} startIcon={<AddIcon />}
                                            > Add class</MDButtonRoot>
                                            <DataGrid
                                                rows={classes}
                                                columns={columns}
                                                getRowId={(row) => row.id}
                                                initialState={{
                                                    pagination: {
                                                        paginationModel: { page: 0, pageSize: 100 },
                                                    },
                                                }}
                                                pageSizeOptions={[100]}

                                                editMode="row"
                                            />
                                        </StyledBox>
                                        {isModalOpen && (
                                            <EditClassDialog rowData={editedRow} onSave={handleSave} onClose={() => setIsModalOpen(false)} />
                                        )}

                                        {isDeleteDialogOpen && (<DeleteClassDialog rowData={editedRow} onClose={() => setIsDeleteDialogOpen(false)} />)}


                                        {provided.placeholder}
                                    </MDBoxRoot>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </MDBoxRoot></Card></Grid></Grid>
    )
}
export default ClassesList;

interface DeleteClassDialogProps {
    rowData: any;
    onClose: () => void;
}
const DeleteClassDialog: FC<DeleteClassDialogProps> = ({ rowData, onClose }) => {

    const { loading, data: cl_events_data } = useQuery(getEventsForClassQuery, { variables: { id: rowData.id }, fetchPolicy: 'network-only' });
    const [deleteClassMutation, { data: del_data, loading: del_loading, error: del_error }] = useMutation(deleteClassQuery, {
        refetchQueries: [
            'GetClasses' // Query name
        ],
    });

    const linkedEventsCount = cl_events_data?.get_event_count_for_class;

    const handleDeleteClass = async () => {
        const id = rowData.id;
        await deleteClassMutation({ variables: { filter: { id: { eq: id } } } })
        onClose();
    };


    return (
        <Dialog
            maxWidth="sm"
            open={!loading}
            fullWidth
        >
            <MDTypographyRoot variant="h5" px={1} py={1}>Delete class? <CloseIcon onClick={onClose} sx={{ position: "absolute", top: "10px", right: "10px" }} /></MDTypographyRoot>
            <DialogContent dividers>
                <MDTypographyRoot variant="h4" px={1} ownerState={{ color: "dark" }}>
                    {linkedEventsCount === '0' && `Deleting class "${rowData.name}"`}
                    {linkedEventsCount !== '0' && `Class "${rowData.name}" has ${linkedEventsCount} linked events. Delete events before deleting the class.`}
                </MDTypographyRoot>
            </DialogContent>
            <DialogActions>
                {linkedEventsCount === '0' && (
                    <MDButtonRoot
                        ownerState={{
                            variant: "gradient",
                            color: "info",
                            textTransform: "none",
                            circular: false,
                            iconOnly: false,
                            darkMode: false
                        }}
                        size="medium"
                        onClick={handleDeleteClass}
                        type="submit">
                        Delete
                    </MDButtonRoot>
                )}
                <MDButtonRoot
                    size="medium"
                    onClick={onClose}
                    ownerState={{
                        textTransform: "none",
                        variant: "gradient",
                        color: "warning",
                    }}>
                    Cancel
                </MDButtonRoot>
            </DialogActions>
        </Dialog>);
}
